import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';

/**
 * Clean up duplicate regulatory deadline records
 * Keeps only one record per regulator/trigger combination
 */
export const cleanupDuplicateRegulatoryDeadlines = async (incidentId, orgId, db) => {
  try {
    console.log('🧹 Starting cleanup of duplicate regulatory deadlines...');
    
    // Fetch all regulatory deadlines for this incident
    const regulatoryDeadlinesRef = collection(db, 'c3_regulatory_deadlines');
    const regulatoryQuery = query(
      regulatoryDeadlinesRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId),
      where('deleted', '==', false)
    );
    
    const snapshot = await getDocs(regulatoryQuery);
    const deadlines = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('📊 Found regulatory deadlines:', deadlines.length);
    
    // Group by regulator + trigger combination
    const groups = {};
    deadlines.forEach(deadline => {
      const key = `${deadline.regulator}-${deadline.trigger}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(deadline);
    });
    
    console.log('📋 Grouped deadlines:', Object.keys(groups).map(key => ({
      key,
      count: groups[key].length
    })));
    
    // Find duplicates and delete extras
    let deletedCount = 0;
    const deletePromises = [];
    
    for (const [key, groupDeadlines] of Object.entries(groups)) {
      if (groupDeadlines.length > 1) {
        console.log(`🔍 Found ${groupDeadlines.length} duplicates for ${key}`);
        
        // Sort by creation time and keep the oldest one
        groupDeadlines.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(0);
          return aTime - bTime;
        });
        
        // Delete all except the first (oldest) one
        for (let i = 1; i < groupDeadlines.length; i++) {
          const deadlineToDelete = groupDeadlines[i];
          console.log(`🗑️ Deleting duplicate deadline: ${deadlineToDelete.id} (${key})`);
          
          const deletePromise = deleteDoc(doc(db, 'c3_regulatory_deadlines', deadlineToDelete.id));
          deletePromises.push(deletePromise);
          deletedCount++;
        }
      }
    }
    
    // Execute all deletions
    if (deletePromises.length > 0) {
      await Promise.all(deletePromises);
      console.log(`✅ Deleted ${deletedCount} duplicate regulatory deadlines`);
    } else {
      console.log('ℹ️ No duplicates found to delete');
    }
    
    return {
      totalFound: deadlines.length,
      duplicatesDeleted: deletedCount,
      uniqueRemaining: deadlines.length - deletedCount
    };
    
  } catch (error) {
    console.error('❌ Error cleaning up duplicate regulatory deadlines:', error);
    throw error;
  }
};

/**
 * Get duplicate regulatory deadlines report without deleting
 */
export const getDuplicateRegulatoryDeadlinesReport = async (incidentId, orgId, db) => {
  try {
    const regulatoryDeadlinesRef = collection(db, 'c3_regulatory_deadlines');
    const regulatoryQuery = query(
      regulatoryDeadlinesRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId),
      where('deleted', '==', false)
    );
    
    const snapshot = await getDocs(regulatoryQuery);
    const deadlines = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Group by regulator + trigger combination
    const groups = {};
    deadlines.forEach(deadline => {
      const key = `${deadline.regulator}-${deadline.trigger}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(deadline);
    });
    
    const duplicates = Object.entries(groups).filter(([key, group]) => group.length > 1);
    
    return {
      totalDeadlines: deadlines.length,
      uniqueCombinations: Object.keys(groups).length,
      duplicateGroups: duplicates.length,
      duplicates: duplicates.map(([key, group]) => ({
        key,
        count: group.length,
        deadlines: group.map(d => ({
          id: d.id,
          regulator: d.regulator,
          trigger: d.trigger,
          createdAt: d.createdAt?.toDate?.() || null
        }))
      }))
    };
    
  } catch (error) {
    console.error('❌ Error getting duplicate regulatory deadlines report:', error);
    throw error;
  }
}; 